/** Menu maths shared by every screen: selection that skips disabled items, and hit-testing. Pure. */

export interface MenuItem {
  readonly id: string;
  readonly label: string;
  readonly disabled?: boolean;
}

export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

export function inRect(r: Rect, x: number, y: number): boolean {
  return x >= r.x && x < r.x + r.w && y >= r.y && y < r.y + r.h;
}

/** `i` wrapped into 0..n-1. */
export function wrapIndex(i: number, n: number): number {
  return n <= 0 ? 0 : ((i % n) + n) % n;
}

/** `i` clamped into 0..n-1. */
export function clampIndex(i: number, n: number): number {
  return Math.max(0, Math.min(n - 1, i));
}

/** The next enabled item `delta` steps away (wrapping, or stopping at the ends); `sel` if there is none. */
export function moveSelection(
  items: readonly { readonly disabled?: boolean }[], sel: number, delta: number, wrap = true,
): number {
  const n = items.length;
  if (n === 0 || delta === 0) return sel;
  const step = delta > 0 ? 1 : -1;
  let i = sel;
  for (let k = 0; k < n; k++) {
    const next = i + step;
    if (!wrap && (next < 0 || next >= n)) return sel;
    i = wrapIndex(next, n);
    if (items[i]?.disabled !== true) return i;
  }
  return sel;
}

/** The first enabled index at or after `from`. */
export function firstEnabled(items: readonly { readonly disabled?: boolean }[], from = 0): number {
  for (let k = 0; k < items.length; k++) {
    const i = wrapIndex(from + k, items.length);
    if (items[i]?.disabled !== true) return i;
  }
  return 0;
}

/** A vertical list: text rows start at `top`, one every `rowH`; each row's hit area is centred on its text. */
export interface ListLayout {
  readonly top: number;
  readonly rowH: number;
  /** Hit area left edge and width. */
  readonly x: number;
  readonly w: number;
}

/** The hit rectangle of row `i`. */
export function rowRect(L: ListLayout, i: number): Rect {
  const pad = Math.floor((L.rowH - 8) / 2);
  return { x: L.x, y: L.top + i * L.rowH - pad, w: L.w, h: L.rowH };
}

/** The row under (x, y), or -1. */
export function rowAt(L: ListLayout, count: number, x: number, y: number): number {
  if (x < L.x || x >= L.x + L.w) return -1;
  const pad = Math.floor((L.rowH - 8) / 2);
  const i = Math.floor((y - (L.top - pad)) / L.rowH);
  return i >= 0 && i < count ? i : -1;
}

/** Scroll so that row `sel` of `count` is inside a window of `visible` rows, given the current `scroll`. */
export function scrollToShow(sel: number, scroll: number, visible: number, count: number): number {
  let s = scroll;
  if (sel < s) s = sel;
  if (sel >= s + visible) s = sel - visible + 1;
  return Math.max(0, Math.min(Math.max(0, count - visible), s));
}
